var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { ProjectType, StudyVersionStatus } from "healthex-common/study.dtos";
import { pick } from "healthex-common/utils";
import { computed, observable } from "mobx";
import { NOT_LOADED } from "../Infra/RemoteData";
import { validators } from "../Logic/Validators";
import { FormModel } from "./FormModel";
export const WEBHOOK_EVENT_OPTIONS = [
    {
        id: "consent.withdrawn",
        description: "Emitted when a patient revokes authorization for one of your projects.",
        comingSoon: false,
    },
    {
        id: "consent.granted",
        description: "Emitted when a patient authorizes one of your projects.",
        comingSoon: true,
    },
];
export const SELECTABLE_WEBHOOK_EVENT_IDS = WEBHOOK_EVENT_OPTIONS.filter((event) => !event.comingSoon).map((event) => event.id);
const isHttpsUrlOrLocalhost = (value) => {
    try {
        const parsed = new URL(value.trim());
        if (parsed.protocol === "https:") {
            return true;
        }
        return parsed.protocol === "http:" && parsed.hostname === "localhost";
    }
    catch {
        return false;
    }
};
export class AddOrEditWebhookFormModel {
    constructor(opts) {
        this.projectOptions = NOT_LOADED;
        this.formModel = new FormModel({
            name: "",
            endpointUrl: "",
            scopeMode: "SINGLE",
            singleProject: null,
            multipleProjects: [],
            subscribedEvents: [...SELECTABLE_WEBHOOK_EVENT_IDS],
            sendTestEvent: true,
        }, ["name", "endpointUrl", "subscribedEvents"]);
        this.studyManager = opts.container.studyManager;
        this.provisioningService = opts.container.provisioningService;
        this.authManager = opts.container.authManager;
        this.onCreated = opts.onCreated;
        this.onUpdated = opts.onUpdated;
        this.onCancel = opts.onCancel;
        if (opts.webhookToEdit) {
            this.webhookToEdit = opts.webhookToEdit;
            this.formModel.input.name = opts.webhookToEdit.name;
            this.formModel.input.endpointUrl = opts.webhookToEdit.webhookUrl;
            this.formModel.input.scopeMode =
                opts.webhookToEdit.studyScopeProjects === null
                    ? "ORG_WIDE"
                    : opts.webhookToEdit.studyScopeProjects.length === 1
                        ? "SINGLE"
                        : "MULTIPLE";
            if (this.formModel.input.scopeMode === "SINGLE") {
                this.formModel.input.singleProject = {
                    id: opts.webhookToEdit.studyScopeProjects[0].id,
                    title: opts.webhookToEdit.studyScopeProjects[0].title,
                };
            }
            else if (this.formModel.input.scopeMode === "MULTIPLE") {
                this.formModel.input.multipleProjects = opts.webhookToEdit.studyScopeProjects.map((study) => ({
                    id: study.id,
                    title: study.title,
                }));
            }
            this.formModel.input.subscribedEvents = opts.webhookToEdit.eventTypes;
            this.formModel.input.sendTestEvent = false;
        }
        void this.loadProjects();
    }
    get scopeMode() {
        return this.formModel.input.scopeMode;
    }
    get isEditing() {
        return this.webhookToEdit !== undefined;
    }
    cancel() {
        var _a;
        (_a = this.onCancel) === null || _a === void 0 ? void 0 : _a.call(this);
    }
    async createWebhook() {
        var _a, _b;
        this.formModel.clearErrors();
        this.formModel.validateField("name", validators.notBlank, "Enter a webhook name");
        this.formModel.validateField("endpointUrl", validators.notBlank, "Enter an endpoint URL");
        this.formModel.validateField("endpointUrl", isHttpsUrlOrLocalhost, "Endpoint must be a valid HTTPS URL");
        if (this.formModel.input.scopeMode === "SINGLE" && !this.formModel.input.singleProject) {
            this.formModel.addError("singleProject", "Select a project");
        }
        if (this.formModel.input.scopeMode === "MULTIPLE" && this.formModel.input.multipleProjects.length === 0) {
            this.formModel.addError("multipleProjects", "Select at least one project");
        }
        if (this.formModel.input.subscribedEvents.length === 0) {
            this.formModel.addError("subscribedEvents", "Select at least one event");
        }
        if (this.formModel.hasErrors())
            return;
        if (!this.authManager.orgId)
            return;
        const commonInput = {
            name: this.formModel.input.name,
            webhookUrl: this.formModel.input.endpointUrl,
            studyScope: this.formModel.input.scopeMode === "ORG_WIDE"
                ? null
                : this.formModel.input.scopeMode === "SINGLE"
                    ? [this.formModel.input.singleProject.id]
                    : this.formModel.input.multipleProjects.map((project) => project.id),
            eventTypes: this.formModel.input.subscribedEvents,
        };
        if (this.isEditing) {
            const updateInput = commonInput;
            await this.provisioningService.updateWebhookConfiguration(this.authManager.orgId, this.webhookToEdit.id, updateInput);
            (_a = this.onUpdated) === null || _a === void 0 ? void 0 : _a.call(this);
        }
        else {
            const createInput = commonInput;
            const created = await this.provisioningService.createWebhookConfiguration(this.authManager.orgId, createInput);
            (_b = this.onCreated) === null || _b === void 0 ? void 0 : _b.call(this, created, this.formModel.input.sendTestEvent);
        }
    }
    async loadProjects() {
        const { results } = await this.studyManager.fetchStudies(StudyVersionStatus.ACTIVE, {
            projectTypes: [ProjectType.GENERAL_DATA_USE, ProjectType.AI_DATA_USE],
        });
        this.projectOptions = results.map((study) => pick(study, "id", "title"));
    }
}
__decorate([
    observable
], AddOrEditWebhookFormModel.prototype, "projectOptions", void 0);
__decorate([
    computed
], AddOrEditWebhookFormModel.prototype, "scopeMode", null);
__decorate([
    computed
], AddOrEditWebhookFormModel.prototype, "isEditing", null);
